import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import { setBreadcrumb } from '../store/slices/breadcrumbSlice';
import { checkAuthToken } from '../services/auth/auth';
import { parseStaticURLToDynamicValues, buildQuestionsInvitePath } from '../utils/helpers';
import {
  CLIENT_ADMIN_ROLE,
  OFFICE_ADMIN_ROLE,
  OFFICE_USER_ROLE,
  CLIENT_USER_ROLE,
  SUPER_ADMIN_ROLE,
  REALTOR_ROLE,
  DYNAMIC_BREADCRUMB_ROUTES,
} from '../utils/constants';
import { logout } from '../store/slices/authSlice';
import { removeBankDetails, removeCurrentStep } from '../store/slices/questionSlice';
import SessionExpiredModal from '../components/common/SessionExpiredModal';

const PrivateRoute = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  const { code, participantNo, memberUUID } = params;
  const data = useSelector((state) => state?.authSlice);
  const order_part_participant_id = useSelector(
    (state) => state.questionSlice.questionDetails
  )?.find((que) => que.tx_id === code)?.order_part_participant_id;
  const isOfficeAdmin =
    props?.type === 'admin'
      ? data['admin']?.user_role_name === OFFICE_ADMIN_ROLE
        ? true
        : false
      : false;
  const isClientAdmin =
    props?.type === 'admin'
      ? data['admin']?.user_role_name === CLIENT_ADMIN_ROLE
        ? true
        : false
      : false;
  const isSuperAdmin =
    props?.type === 'admin'
      ? data['admin']?.user_role_name === SUPER_ADMIN_ROLE
        ? true
        : false
      : false;
  const isUser =
    props?.type === 'order'
      ? data['order']?.user_role_name === OFFICE_USER_ROLE ||
        data['order']?.user_role_name === CLIENT_USER_ROLE
        ? true
        : false
      : false;
  const isRealtor =
    props?.type === 'rx' ? data['rx']?.user_role_name === REALTOR_ROLE : false;
  const [authenticated, setAuthenticated] = useState(true);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (props.onlySuperAdminAccess && !isSuperAdmin) {
      navigate('/admin/dashboard');
    }
    if (props.onlyAdminAccess && isClientAdmin) {
      navigate('/admin/dashboard');
    }
    if (props.onlyUserAccess && !isUser) {
      navigate('/admin/dashboard');
    }
    if (props.type === 'rx' && !isRealtor && data['rx']?.token) {
      navigate('/login');
    }
    if (props.clientLevelAccess && isOfficeAdmin) {
      navigate('/admin/dashboard');
    }
  }, [pathname]);

  useEffect(() => {
    checkAuthenticated();
  }, [pathname, search]);

  // Listen for session expiration events from API calls
  const handleSessionExpired = useCallback(() => {
    if (!showSessionExpiredModal) setShowSessionExpiredModal(true);
    setAuthenticated(false);
  }, []);

  useEffect(() => {
    window.addEventListener('sessionExpired', handleSessionExpired);
    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, [handleSessionExpired]);

  // Helper function to check if current path should use dynamic breadcrumbs
  const shouldUseDynamicBreadcrumbs = (currentPath) => {
    return DYNAMIC_BREADCRUMB_ROUTES.some((route) => currentPath?.startsWith(route));
  };

  // Dispatch the setBreadcrumb action with the provided title
  useEffect(() => {
    const updatedBreadcrumb = props?.breadcrumb?.map((item) => {
      if (Object.keys(params || {}).length) {
        return parseStaticURLToDynamicValues(item, params);
      }
      return item;
    });

    // Only set breadcrumb if the current path doesn't use dynamic breadcrumbs
    if (!shouldUseDynamicBreadcrumbs(pathname)) {
      dispatch(setBreadcrumb({ data: updatedBreadcrumb, key: pathname }));
    }
  }, [dispatch, props?.breadcrumb, pathname]);

  const checkAuthenticated = async () => {
    let user_id = '';
    let token = '';
    if (props.type === 'participant') {
      token = data[code]?.token;
      user_id = data[code]?.id;
    } else {
      token = data[props?.type]?.token;
      user_id = data[props?.type]?.id;
    }
    if (!token) {
      dispatch(
        logout({
          url: window.location.origin,
          code: code ? code : 'admin',
        })
      );
      if (code && participantNo && +participantNo !== 1) {
        dispatch(
          removeCurrentStep({
            tx_id: code,
          })
        );
        dispatch(
          removeBankDetails({
            tx_id: code,
          })
        );
      }
      return setAuthenticated(false);
    }
    let payload = { user_id };
    if (pathname.split('/')[1] === 'questions') payload.participant_no = participantNo || 1;
    if (order_part_participant_id) payload.order_part_participant_id = order_part_participant_id;
    if (memberUUID) payload.member_uuid = memberUUID;
    const res = await checkAuthToken(payload, token);
    if (res?.success) {
      setAuthenticated(true);
      setShowSessionExpiredModal(false);

      // below logic is for storing the last path of the user
      if (pathname.split('/')[1] !== 'questions') {
        const url = window.location.origin;
        const lastPathKey = `last_path_${url}`;
        if (user_id) {
          const addedPaths = JSON.parse(localStorage.getItem(lastPathKey) || '{}');
          localStorage.setItem(
            lastPathKey,
            JSON.stringify({
              ...addedPaths,
              [user_id]: pathname + search,
            })
          );
        }
      }
    } else {
      // Token exists but API check failed - show session expired modal
      // dispatch(
      //   logout({
      //     url: window.location.origin,
      //     code: code ? code : 'admin',
      //   })
      // );
      // if (code && participantNo && +participantNo !== 1) {
      //   dispatch(
      //     removeCurrentStep({
      //       tx_id: code,
      //     })
      //   );
      //   dispatch(
      //     removeBankDetails({
      //       tx_id: code,
      //     })
      //   );
      // }
      setShowSessionExpiredModal(true);
      setAuthenticated(false);
    }
  };

  const handleNavigateToLogin = () => {
    setShowSessionExpiredModal(false);
    dispatch(
      logout({
        url: window.location.origin,
        code: code ? code : 'admin',
      })
    );
    if (code && participantNo && +participantNo !== 1) {
      dispatch(
        removeCurrentStep({
          tx_id: code,
        })
      );
      dispatch(
        removeBankDetails({
          tx_id: code,
        })
      );
    }
    if (props.type === 'admin' || props.type === 'order' || props.type === 'rx') {
      navigate('/login');
    } else {
      navigate(buildQuestionsInvitePath(code, participantNo, memberUUID, search ? search : ''));
    }
  };

  return (
    <React.Fragment>
      <SessionExpiredModal
        open={showSessionExpiredModal}
        onNavigateToLogin={handleNavigateToLogin}
      />
      {props.type === 'admin' ? (
        authenticated || showSessionExpiredModal ? (
          props.component
        ) : (
          <Navigate to={`/login`} />
        )
      ) : props.type === 'order' || props.type === 'rx' ? (
        authenticated || showSessionExpiredModal ? (
          props.component
        ) : (
          <Navigate to={`/login`} />
        )
      ) : authenticated || showSessionExpiredModal ? (
        props.component
      ) : (
        <Navigate
          to={buildQuestionsInvitePath(code, participantNo, memberUUID, search ? search : '')}
        />
      )}
    </React.Fragment>
  );
};

export default PrivateRoute;
